import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import * as Sentry from '@sentry/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { BaseRequest, BaseResponse } from '../models'
import axios from 'axios'
import { FileKind } from '../../models/File'
import { XMLParser } from 'fast-xml-parser'

const _BASE_URL = import.meta.env.REACT_APP_API_BASE_URL!

export interface UploadFileRequest {
  uploadUrl: string
  uploadParameters: { [key: string]: any }
  file: File
}

export interface FileUploadRequest extends BaseRequest {
  body: {
    fileName: string
    fileSize: number
    contentType: string
    kind: FileKind
    recipientUserId?: string
    metadata?: string
  }
}

export interface FileInfo {
  fileId: string
  fileName: string
  fileSize: number
  userId: string
  contentType: string
  kind: FileKind
  metadata: string
  status: string
}

export interface FileDownloadInfoRequest extends BaseRequest {
  fileId: string
}

export interface FileDownloadInfo {
  fileInfo: FileInfo
  location: string
}

export interface FileUploadRequestResponse {
  fileInfo: FileInfo
  fileUploadUrl: string
  fileUploadParameters: { [key: string]: any }
}

const xmlParser = new XMLParser()

export const fileApi = createApi({
  reducerPath: 'fileApi',
  baseQuery: fetchBaseQuery({}),
  endpoints: (builder) => ({
    uploadFile: builder.mutation<void, UploadFileRequest>({
      query: (request) => {
        const formData = new FormData()
        for (const [key, value] of Object.entries(request.uploadParameters)) {
          formData.append(key, value)
        }
        formData.append('file', request.file)

        return {
          url: request.uploadUrl,
          body: formData,
          responseHandler: 'content-type',
          formData: true,
          method: 'POST',
        }
      },
      transformErrorResponse: (error, meta): FetchBaseQueryError => {
        // If RTK Query parsed the error data, use it
        if ('data' in error) {
          const data = error.data
          const contentType = meta?.response?.headers.get('content-type')

          // Parse XML responses to extract error details
          if (contentType?.includes('xml') && typeof data === 'string') {
            try {
              const parsed = xmlParser.parse(data)
              Sentry.captureException(new Error('S3 signed URL upload error', { cause: parsed?.Error }))
              return { ...error, data: parsed?.Error }
            } catch {
              return error
            }
          }
        }
        return error
      },
    }),
    downloadFile: builder.query<any, string>({
      query: (location) => ({
        url: location,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
    }),
  }),
})

export const accountsFileApi = createApi({
  reducerPath: 'accountsFileApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  endpoints: (builder) => ({
    downloadGlobalFileRequest: builder.query<BaseResponse<FileDownloadInfo>, FileDownloadInfoRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/files/download/${request.fileId}/`,
        }
      },
    }),
    uploadFileRequest: builder.mutation<BaseResponse<FileUploadRequestResponse>, FileUploadRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/files/upload/`,
          method: 'POST',
          body: request.body,
        }
      },
    }),
  }),
})

export const downloadFile = (url: string) => {
  return axios.get(url, {
    method: 'GET',
    responseType: 'blob',
  })
}

export const { useUploadFileMutation, useLazyDownloadFileQuery } = fileApi
export const { useLazyDownloadGlobalFileRequestQuery, useUploadFileRequestMutation } = accountsFileApi
